import React, { useState, useCallback, useEffect } from "react";
import {
  Upload,
  FileSpreadsheet,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  Zap,
} from "lucide-react";

const Forecasting = () => {
  const [files, setFiles] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [apiData, setApiData] = useState(null);

  const handleFileUpload = useCallback((event) => {
    const uploadedFiles = event.target.files;
    if (!uploadedFiles || uploadedFiles.length === 0) return;

    const fileList = Array.from(uploadedFiles);
    setFiles(fileList);
    setApiData(null);
  }, []);

  const getQualityColor = (score) => {
    const numScore = parseFloat(score);
    if (numScore >= 90) return "#22c55e";
    if (numScore >= 70) return "#f59e0b";
    return "#ef4444";
  };

  const callForecastingAPI = async (file) => {
    try {
      const formData = new FormData();
      formData.append("folder", file);

      const response = await fetch(
        "https://apiowlsight.bluegecko.ai:8003/analyze_supplier_data/",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return {
        overall_quality_score: data.overall_quality_score,
        report_url: data.report_url,
        results: data.results,
        statistics: {
          total_columns: data.results.length,
          average_score: data.overall_quality_score,
          critical_issues: data.results.filter((r) => r.llm_score < 50).length,
          warnings: data.results.filter(
            (r) => r.llm_score >= 50 && r.llm_score < 70
          ).length,
          passed: data.results.filter((r) => r.llm_score >= 70).length,
        },
      };
    } catch (error) {
      console.error("Error calling forecasting API:", error);
      throw error;
    }
  };

  const analyzeForecasting = useCallback(async () => {
    if (files.length === 0) {
      return;
    }

    setIsAnalyzing(true);

    try {
      const forecastingResponse = await callForecastingAPI(files[0]);
      setApiData(forecastingResponse);
    } catch (error) {
      console.error("Analysis error:", error);
    } finally {
      setIsAnalyzing(false);
    }
  }, [files]);

  return (
    <div className="space-y-8">
      {/* Control Bar */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-xl border border-gray-200 space-y-2">
        <div className="grid grid-cols-12 gap-3">
          {/* File Upload */}
          <div className="col-span-6">
            <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
              Upload ZIP File
            </label>
            <label className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-gray-300 cursor-pointer hover:border-blue-400 transition-colors shadow-sm text-sm">
              <div className="flex items-center gap-2">
                <Upload className="w-3 h-3 text-blue-600" />
                <span className="text-xs font-medium text-gray-700">
                  {files.length > 0 ? files[0].name : "Choose ZIP File"}
                </span>
              </div>

              <input
                type="file"
                className="hidden"
                accept=".zip"
                onChange={handleFileUpload}
                multiple={false}
                key={files.length}
              />
            </label>
          </div>

          {/* Analyze Button */}
          <div className="col-span-6 flex items-end">
            <button
              onClick={analyzeForecasting}
              disabled={isAnalyzing || files.length === 0}
              className={`w-full px-3 py-2 rounded-lg flex items-center justify-center gap-2 font-semibold text-xs transition-all ${
                isAnalyzing || files.length === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:shadow-lg"
              }`}
            >
              {isAnalyzing ? (
                <>
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Analyzing
                </>
              ) : (
                <>
                  <TrendingUp className="w-3 h-3" />
                  Analyze
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      {!apiData && files.length === 0 ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center bg-white p-16 rounded-3xl border border-gray-200 max-w-md w-full shadow-lg">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Zap className="w-10 h-10 text-blue-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Supplier Data Analysis
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Upload a ZIP file containing SAP supplier templates to analyze
              data quality
            </p>
          </div>
        </div>
      ) : (
        <>
          {apiData && (
            <>
              {/* Results Table */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-2">
                <div className="p-8 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900">
                    Column Analysis Results
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <div className="max-h-[600px] overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0">
                        <tr>
                          <th className="px-8 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                            Table
                          </th>
                          <th className="px-8 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                            Column
                          </th>
                          <th className="px-8 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                            LLM Score
                          </th>
                          <th className="px-8 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                            Issues
                          </th>
                          <th className="px-8 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                            Suggestions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {apiData.results?.map((result, idx) => (
                          <tr
                            key={idx}
                            className="hover:bg-blue-50 transition-colors"
                          >
                            <td className="px-8 py-5 text-sm font-semibold text-gray-900">
                              {result.table}
                            </td>
                            <td className="px-8 py-5 text-sm text-gray-700">
                              {result.column}
                            </td>
                            <td className="px-8 py-5 text-center">
                              <span
                                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold"
                                style={{
                                  backgroundColor: `${getQualityColor(
                                    result.llm_score
                                  )}15`,
                                  color: getQualityColor(result.llm_score),
                                }}
                              >
                                {result.llm_score}
                              </span>
                            </td>
                            <td className="px-8 py-5 text-sm text-gray-600">
                              <ul className="list-disc list-inside space-y-1">
                                {result.issues?.slice(0, 2).map((issue, i) => (
                                  <li key={i} className="text-xs">
                                    {issue}
                                  </li>
                                ))}
                                {result.issues?.length > 2 && (
                                  <li className="text-xs text-blue-600">
                                    +{result.issues.length - 2} more
                                  </li>
                                )}
                              </ul>
                            </td>
                            <td className="px-8 py-5 text-sm text-gray-600">
                              <ul className="list-disc list-inside space-y-1">
                                {result.suggestions
                                  ?.slice(0, 2)
                                  .map((suggestion, i) => (
                                    <li key={i} className="text-xs">
                                      {suggestion}
                                    </li>
                                  ))}
                                {result.suggestions?.length > 2 && (
                                  <li className="text-xs text-blue-600">
                                    +{result.suggestions.length - 2} more
                                  </li>
                                )}
                              </ul>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Forecasting;

